import Col from 'antd/es/col'
import Form from 'antd/es/form'
import message from 'antd/es/message'
import notification from 'antd/es/notification'
import Row from 'antd/es/row'
import TreeSelect from 'antd/es/tree-select'
import { LiquidateReasonsTreeData } from './liquidate-idea.def.ts'
import { useLiquidateIdeaMutation } from '../../../../../apis/idea/idea-api.api.ts'
import { IdeyaButton } from '../../../../../common/antd/ideya-button/ideya-button.tsx'
import { FormProps } from 'antd/lib/index'
import { useCallback, useEffect } from 'react'
import { getErrorDescription } from '../../../../../redux/utils.ts'
import { useTranslation } from 'react-i18next'

interface LiquidateIdeaFormFieldTypes {
  id: string
  fillStrategy: string
  liquidateReason: string
}

interface LiquidateIdeaFormProps {
  ideaId: string
  fillStrategy: string
  onCancel: () => void
  onSuccess?: () => void
  onIdeaActioned: () => void
}

export const LiquidateIdeaForm: React.FC<LiquidateIdeaFormProps> = ({
  ideaId,
  fillStrategy,
  onCancel,
  onSuccess,
  onIdeaActioned,
}) => {
  const { t } = useTranslation()
  const [liquidateIdea, { isSuccess, isLoading, isError, error, reset }] =
    useLiquidateIdeaMutation()
  const [form] = Form.useForm<LiquidateIdeaFormFieldTypes>()
  const [messageApi, messageContextHolder] = message.useMessage()
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()

  const onFinish: FormProps<LiquidateIdeaFormFieldTypes>['onFinish'] = (
    values
  ) => {
    liquidateIdea({
      id: ideaId,
      fillStrategy: fillStrategy,
      liquidateReason: Array.isArray(values.liquidateReason)
        ? values.liquidateReason.join(',')
        : values.liquidateReason,
    })
  }

  const success = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: 'Idea liquidated successfully!',
    })
  }, [messageApi])

  const errorNotification = useCallback(
    (description: string) => {
      notificationApi.error({
        message: 'Error',
        description,
        placement: 'topRight',
      })
    },
    [notificationApi]
  )

  useEffect(() => {
    if (isSuccess) {
      success()
      onCancel()
      if (onSuccess) onSuccess()
      if (onIdeaActioned) onIdeaActioned()
      reset()
    }
  }, [isSuccess, form, onCancel, onSuccess, reset, success, onIdeaActioned])

  useEffect(() => {
    if (isError) {
      const description = getErrorDescription(error)
      errorNotification(description)
      reset()
    }
  }, [isError, error, reset, errorNotification])

  return (
    <>
      {messageContextHolder}
      {notificationContextHolder}
      <Form layout={'vertical'} onFinish={onFinish} form={form}>
        <Row>
          <Col span={24}>
            <Form.Item
              label={t(
                'idea_list.idea_modal.liquidate_idea_form.please_provide_the_reason_for_liquidation'
              )}
              name={'liquidateReason'}
              rules={[
                {
                  required: true,
                  message: t(
                    'idea_list.idea_modal.liquidate_idea_form.please_provide_the_reason_for_liquidation'
                  ),
                },
              ]}
            >
              <TreeSelect
                showSearch
                style={{ width: '100%' }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                placeholder={'Select trade reason'}
                allowClear
                multiple
                treeDefaultExpandAll
                treeData={LiquidateReasonsTreeData}
                treeCheckable={true}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row justify={'end'}>
          <Col>
            <Row gutter={[8, 8]}>
              <Col>
                <IdeyaButton ghost onClick={onCancel}>
                  {t('common.cancel')}
                </IdeyaButton>
              </Col>
              <Col>
                <IdeyaButton
                  danger
                  type={'primary'}
                  htmlType={'submit'}
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {t('idea_list.idea_modal.liquidate_idea_form.liquidate')}
                </IdeyaButton>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </>
  )
}
